/* Hallmark component scope: inherited tokens; pre-emit P4 H4 E4 S5 R5 V3. */
import React, { useEffect, useMemo, useRef } from 'react'
import { Check, X } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { useEditorStore } from '../../store/editorStore'
import { structuralGeometryToBoxes } from '../../utils/structuralGeometry.logic'
import { previewRowFootprint } from '../../utils/rowFootprint.logic'
import './FootprintChangeModal.css'

export const FootprintChangeModal: React.FC<{ rackId: string; field: 'widthM' | 'depthM'; value: number; onClose: () => void }> = ({ rackId, field, value, onClose }) => {
  const dialog = useRef<HTMLDialogElement>(null)
  const previousFocus = useRef(document.activeElement as HTMLElement | null)
  const { rack, rows, racks, dcSettings, applyRowFootprint } = useEditorStore(useShallow(state => ({ rack: state.racks[rackId], rows: state.rows, racks: state.racks, dcSettings: state.dcSettings, applyRowFootprint: state.applyRowFootprint })))
  const row = rack?.rowId ? rows[rack.rowId] : undefined
  const plan = useMemo(() => row && rack ? previewRowFootprint({
    row, rack, rowRacks: Object.values(racks).filter(item => item.rowId === row.id), allRacks: Object.values(racks),
    obstacles: structuralGeometryToBoxes(dcSettings.structuredGeometry), field, value, floorPlan: dcSettings.floorPlan,
  }) : null, [row, rack, racks, dcSettings, field, value])
  useEffect(() => {
    const element = dialog.current
    element?.showModal()
    return () => {
      element?.close()
      requestAnimationFrame(() => { if (previousFocus.current?.isConnected) previousFocus.current.focus() })
    }
  }, [])
  if (!rack || !row || !plan) return null
  const apply = () => {
    if (!plan.canApply) return
    applyRowFootprint({ row: plan.nextRow, rackId, targetPatch: plan.targetPatch, rackUpdates: plan.rackUpdates })
    onClose()
  }
  const affected = [{ id: rackId, patch: plan.targetPatch }, ...plan.rackUpdates]
    .sort((a, b) => (racks[a.id].rackPosInRow ?? 0) - (racks[b.id].rackPosInRow ?? 0))
  const point = (position: { xM: number; yM: number } | undefined) => position
    ? `X ${position.xM.toFixed(3)} · Y ${position.yM.toFixed(3)} м` : 'Не задано'
  return <dialog ref={dialog} className="footprint-change" aria-labelledby="footprint-title" aria-describedby="footprint-boundary"
    onCancel={event => { event.preventDefault(); onClose() }} onKeyDown={event => {
      event.stopPropagation()
      if (event.key !== 'Tab') return
      const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); buttons[0]?.focus() }
    }}>
    <header><div><h2 id="footprint-title">Изменение размеров в ряду</h2><p>{row.name} · {rack.label}</p></div>
      <button onClick={onClose} aria-label="Закрыть изменение размеров" title="Закрыть изменение размеров" autoFocus><X size={18} /></button>
    </header>
    <main>
      <div className="footprint-change-summary"><span>{field === 'widthM' ? 'Ширина' : 'Глубина'}, м</span>
        <strong>{rack[field] == null ? 'Неизвестно' : rack[field].toFixed(3)} → {value.toFixed(3)}</strong>
        <span>Перемещаемых соседей: {plan.rackUpdates.length} · Адреса и порядок мест сохраняются</span>
      </div>
      <table aria-label="Изменения размещения"><thead><tr><th scope="col">Стойка / место</th><th scope="col">До</th><th scope="col">После</th></tr></thead>
        <tbody>{affected.map(({ id, patch }) => {
          const before = racks[id]
          return <tr key={id}><th scope="row">{before.label}<small>{before.rackPosInRow != null ? `Место ${before.rackPosInRow}` : 'Место не подтверждено'} · {id === rackId ? 'Размер' : 'Сдвиг'}</small></th>
            <td>{point({ xM: before.x / 100, yM: before.y / 100 })}</td>
            <td>{point({ xM: (patch.x ?? before.x) / 100, yM: (patch.y ?? before.y) / 100 })}</td></tr>
        })}</tbody>
      </table>
      <dl className="footprint-change-anchors"><dt>Начало ряда</dt><dd>{point(row.head)} → {point(plan.nextRow.head)}</dd>
        <dt>Конец ряда</dt><dd>{point(row.tail)} → {point(plan.nextRow.tail)}</dd></dl>
      {plan.errors.length > 0 ? <div role="alert" className="footprint-change-error">{plan.errors.map(error => <p key={error}>{error}</p>)}</div>
        : <p role="status" className="footprint-change-valid">Коллизий и выхода за границы в предлагаемом изменении не найдено.</p>}
      <p id="footprint-boundary" className="footprint-change-boundary">Опубликованный план не изменяется. Размеры и связанные сдвиги применятся к рабочему черновику одним действием.</p>
    </main>
    <footer><button onClick={onClose}>Отмена</button><button onClick={apply} disabled={!plan.canApply}><Check size={16} /> Применить</button></footer>
  </dialog>
}
